<template>
    <div>
        <common-content-spacer height="small">
            <reservation-filter :filter="filterApplied" @filter-change="filterChanged"></reservation-filter>

            <div v-if="isReservationsReceived" class="my-reservations__user-selector">
                <common-user-selector
                    v-if="selectedPerformerId"
                    :users="availabilityPerformers"
                    :selectedUserId="selectedPerformerId"
                    @user-selected="userSelected($event)"
                ></common-user-selector>
                <div v-if="totalCount">
                    {{ $t('pages.reservations.reservation.reservation_number', { n: totalCount }) }}
                </div>
            </div>

            <reservation-list
                v-if="reservations.length"
                :reservations="reservations"
                :isReservationsReceived="isReservationsReceived"
            >
                <common-buttons v-if="reservations.length < totalCount">
                    <common-button
                        type="primary"
                        :label="$t('common.microcopy.show_more')"
                        :isProcessing="isLoading"
                        @click="loadMore"
                    ></common-button>
                </common-buttons>
            </reservation-list>
            <div v-else-if="!isLoading" class="reservation-list--message">
                {{ $t('pages.reservations.reservation.no_reservation_message') }}
            </div>
        </common-content-spacer>
    </div>
</template>

<script>
    import Store from '~/store'
    import ReservationList from 'components/page-elements/reservations/ReservationList'
    import ReservationFilter from 'components/page-elements/reservations/ReservationFilter'
    import ReservationTypeEnum from 'enums/reservation_type.enum'
    import CalendarEventAPI from 'api/CalendarEventAPI'
    import ReservationSortFilterEnum from 'enums/reservation_sort_filter.enum'
    import AvailabilityAPI from 'api/AvailabilityAPI'
    import RoutesEnum from 'enums/routes.enum'

    export default {
        components: { ReservationList, ReservationFilter },

        data: () => ({
            limit: 10,
            offset: 0,
            totalCount: 0,
            reservations: [],
            isLoading: false,
            filterApplied: {},
            selectedPerformerId: undefined,
            availabilityPerformers: [],
        }),

        computed: {
            isReservationsReceived() {
                return this.$route.params.type === ReservationTypeEnum.RECEIVED
            },
        },

        methods: {
            updateReservations(res, append) {
                this.totalCount = res.total
                if (append) {
                    this.reservations = [...this.reservations, ...res.page]
                    return
                }
                this.reservations = res.page
            },
            loadReservations(type, append) {
                const query = { performerId: this.selectedPerformerId }
                this.isLoading = true

                if (!append) {
                    Store.dispatch('configuration/setIsLoading', true)
                }
                if (this.$route.query.resDateFrom) {
                    query.dateFrom = this.$route.query.resDateFrom
                }

                if (this.$route.query.resDateTo) {
                    query.dateTo = this.$route.query.resDateTo
                }

                if (this.$route.query.status?.length) {
                    if (typeof this.$route.query.status === 'string') {
                        query.status = [this.$route.query.status]
                    } else {
                        query.status = this.$route.query.status
                    }
                }
                if (this.$route.query.resFilter) {
                    query.filter = this.$route.query.resFilter
                }
                if (this.$route.query.excludeOfferIds) {
                    if (Array.isArray(this.$route.query.excludeOfferIds)) {
                        query.excludeOfferIds = this.$route.query.excludeOfferIds.map((offerId) => +offerId)
                    } else {
                        query.excludeOfferIds = [this.$route.query.excludeOfferIds]
                    }
                }

                if (this.$route.query.orderBy) {
                    query.orderBy = this.$route.query.orderBy
                    query.wayDefinition = this.$route.query.wayDefinition
                } else {
                    query.orderBy = ReservationSortFilterEnum.CREATED_AT_DESC.value.orderBy
                    query.wayDefinition = ReservationSortFilterEnum.CREATED_AT_DESC.value.wayDefinition
                }

                query.skip = this.offset
                query.take = this.limit

                if (this.$auth.isSubdomainActive()) {
                    query.companyId = Store.getters['data/getSubdomainCompany'].id
                }

                if (type === ReservationTypeEnum.RECEIVED) {
                    CalendarEventAPI.getReservationsReceived(query)
                        .then((res) => {
                            this.updateReservations(res, append)
                        })
                        .finally(() => ((this.isLoading = false), Store.dispatch('configuration/setIsLoading', false)))
                } else {
                    CalendarEventAPI.getReservationsMade(query)
                        .then((res) => {
                            this.updateReservations(res, append)
                        })
                        .finally(() => ((this.isLoading = false), Store.dispatch('configuration/setIsLoading', false)))
                }
            },

            loadMore() {
                this.offset = this.offset + this.limit
                this.loadReservations(this.$route.params.type, true)
            },

            setFilters() {
                this.selectedPerformerId = this.$route.query?.performerId
                    ? Number(this.$route.query?.performerId)
                    : this.$auth.getUser().id
                this.filterApplied = {
                    performerId: this.selectedPerformerId,
                    resDateFrom: this.$route.query.resDateFrom ? this.$route.query.resDateFrom : undefined,
                    resDateTo: this.$route.query.resDateTo ? this.$route.query.resDateTo : undefined,
                    resFilter: this.$route.query.resFilter ? this.$route.query.resFilter : undefined,
                    excludeOfferIds: Array.isArray(this.$route.query.excludeOfferIds)
                        ? this.$route.query.excludeOfferIds.map((offerId) => +offerId)
                        : [+this.$route.query.excludeOfferIds],
                }

                if (typeof this.$route.query.status === 'string') {
                    this.filterApplied.status = [this.$route.query.status]
                } else {
                    this.filterApplied.status = this.$route.query.status
                }
                if (this.$route.query.orderBy) {
                    this.filterApplied.orderBy = {
                        orderBy: this.$route.query.orderBy,
                        wayDefinition: this.$route.query.wayDefinition,
                    }
                } else {
                    this.filterApplied.orderBy = ReservationSortFilterEnum.UPDATED_AT_DESC.value
                }
                this.loadReservations(this.$route.params.type)
            },

            filterChanged(payload) {
                this.filterApplied = payload
                this.loadReservations(this.$route.params.type)
            },

            onPopState() {
                this.setFilters()
            },

            loadAvailabilityPerformers() {
                const currentUser = this.$auth.getUser()
                this.selectedPerformerId = this.$route.query?.performerId
                    ? Number(this.$route.query?.performerId)
                    : currentUser.id
                AvailabilityAPI.getAvailabilityPerformers().then((res) => {
                    const isCurrentUserPerformer = res.find((u) => u.id === currentUser.id)
                    if (!isCurrentUserPerformer) {
                        this.availabilityPerformers = [...res, currentUser]
                    } else {
                        this.availabilityPerformers = res
                    }
                })
            },

            userSelected(userId) {
                this.selectedPerformerId = userId

                if (this.$auth.isSubdomainActive()) {
                    this.$router.push({
                        name: RoutesEnum.SUBDOMAIN.RESERVATIONS,
                        query: { ...this.$route.query, performerId: this.selectedPerformerId },
                    })
                } else {
                    if (this.$auth.isUserAdmin()) {
                        this.$router.push({
                            name: RoutesEnum.ADMIN.RESERVATIONS,
                            query: { ...this.$route.query, performerId: this.selectedPerformerId },
                        })
                    } else {
                        this.$router.push({
                            name: RoutesEnum.RESERVATIONS.MY,
                            query: { ...this.$route.query, performerId: this.selectedPerformerId },
                        })
                    }
                }
                this.loadReservations(this.$route.params.type)
            },
        },

        watch: {
            '$route.params.type': {
                handler: function (newValue, oldValue) {
                    if (oldValue !== newValue) {
                        this.reservations = []
                        this.offset = 0
                        this.setFilters()
                    }
                },
                deep: true,
                immediate: true,
            },
        },

        beforeUnmount() {
            window.removeEventListener('popstate', this.onPopState)
        },

        mounted() {
            window.addEventListener('popstate', this.onPopState)

            this.loadAvailabilityPerformers()
            if (this.$auth.isSubdomainActive()) {
                this.$auth.reloadSubdomainCompany().then(() => {
                    this.setFilters()
                })
            }
        },
    }
</script>

<style lang="scss" scoped>
    .reservation-list {
        &--message {
            text-align: center;
        }
    }

    .my-reservations {
        &__user-selector {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-left: 10px;
        }
    }
</style>
